import { DetailedHTMLProps, FC, ReactNode, useMemo } from 'react';

import classNames from 'classnames';

import './Table.scss';

import { SpacingOptions, TestMetadata } from '../types';

type responsive = 'xl' | 'lg' | 'md' | 'sm' | 'xs';
type pinnedColumn = 'first' | 'last' | 'both';

export interface TableProps
  extends DetailedHTMLProps<
    React.TableHTMLAttributes<HTMLTableElement>,
    HTMLTableElement
  > {
  /**
   * Table Content
   */
  children?: ReactNode;
  /**
   * Table's classnames.
   */
  className?: string;
  /**
   * Table Wrapper's classnames.
   */
  wrapperClassName?: string;
  /**
   * Use 'xl' | 'lg' | 'md' | 'sm' | 'xs' as needed to create responsive tables up to a particular breakpoint. From that breakpoint and up, the table will behave normally and not scroll horizontally.
   */
  responsive?: responsive;
  /**
   * Table's border. Use 'horizontal' | 'vertical' | true
   */
  bordered?: 'horizontal' | 'vertical' | 'rows' | true;
  /**
   * Removes the borders.
   */
  borderless?: boolean;
  /**
   * Table row hover effect
   */
  hover?: boolean;
  /**
   * Make tables compact by cutting cell padding in half.
   */
  small?: boolean;
  /**
   * Make tables larger by doubling cell padding in half.
   */
  large?: boolean;
  /**
   * Adds zebra-striping to any table row within the <tbody>.
   */
  striped?: boolean;
  /**
   * Pins first, last or both columns
   */
  pinnedColumn?: pinnedColumn;
  /**
   * Any additional component after table.
   */
  afterTable?: ReactNode;
  /**
   * Space in-between rows.
   */
  rowSpace?: SpacingOptions;
  // Test IDs for component sections
  testMetadata?: TestMetadata;
}

const Table: FC<TableProps> = ({
  children,
  responsive,
  bordered,
  borderless,
  hover,
  small,
  large,
  striped,
  className,
  wrapperClassName,
  afterTable,
  pinnedColumn,
  rowSpace,
  testMetadata,
  ...otherProps
}) => {
  const tableClassNames = useMemo(
    () =>
      classNames(
        'sb-table',
        {
          'sb-table-striped': striped,
          'sb-table-borderless': borderless,
          'sb-table-bordered': bordered === true,
          'sb-table-bordered-horizontal': bordered === 'horizontal',
          'sb-table-bordered-vertical': bordered === 'vertical',
          'sb-table-bordered-rows': bordered === 'rows',
          'sb-table-hover': hover,
          'sb-table-small': small,
          'sb-table-large': large,
          'sb-pinned-first-column':
            pinnedColumn === 'first' || pinnedColumn === 'both',
          'sb-pinned-last-column':
            pinnedColumn === 'last' || pinnedColumn === 'both',
          ['sb-table-row-space-' + rowSpace]: rowSpace,
        },
        className,
      ),
    [
      bordered,
      borderless,
      className,
      pinnedColumn,
      hover,
      small,
      large,
      striped,
      rowSpace,
    ],
  );

  const tableWrapperClassNames = useMemo(
    () =>
      classNames(
        {
          [`sb-table-responsive sb-table-responsive-${responsive}`]: responsive,
        },
        wrapperClassName,
      ),
    [responsive, wrapperClassName],
  );

  return (
    <div className={tableWrapperClassNames} {...testMetadata}>
      <table {...otherProps} className={tableClassNames}>
        {children}
      </table>
      {afterTable}
    </div>
  );
};

export default Table;
